/**
 * Wishlist – save things you want to buy later.
 * Wired to GET/POST/DELETE /api/v1/wishlist.
 */
import React, { useEffect, useState } from 'react';
import { wishlistApi, type WishlistItem } from '../services/api';

const Wishlist: React.FC = () => {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newPrice, setNewPrice] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const loadItems = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await wishlistApi.list();
      if (res?.success) {
        setItems(Array.isArray(res?.data) ? res.data : []);
      } else setError(res?.error || 'Failed to load wishlist');
    } catch {
      setError('Failed to load wishlist');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const priceNum = newPrice.trim() ? parseFloat(newPrice) : undefined;
      const res = await wishlistApi.add({
        name: newName.trim(),
        url: newUrl.trim() || undefined,
        imageUrl: newImageUrl.trim() || undefined,
        note: newNote.trim() || undefined,
        priceAtSave: priceNum !== undefined && !Number.isNaN(priceNum) ? priceNum : undefined,
      });
      if (res?.success) {
        setNewName('');
        setNewUrl('');
        setNewImageUrl('');
        setNewNote('');
        setNewPrice('');
        setShowAdd(false);
        loadItems(true);
      } else setError(res?.error || 'Failed to add item');
    } catch {
      setError('Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id: string) => {
    setRemoving(id);
    setError(null);
    try {
      const res = await wishlistApi.remove(id);
      if (res?.success) loadItems(true);
      else setError(res?.error || 'Failed to remove item');
    } catch {
      setError('Failed to remove item');
    } finally {
      setRemoving(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>❤️ Wishlist</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Save things you want to buy later
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowAdd((s) => !s)}
              style={{
                padding: '8px 16px',
                background: 'rgba(76,175,80,0.9)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Add Item
            </button>
            <button
              type="button"
              onClick={() => loadItems(true)}
              disabled={refreshing}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e2e8f0',
        }}>
          <input
            type="text"
            placeholder="Item name (required)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Link to product (optional)"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Image URL (optional)"
            value={newImageUrl}
            onChange={(e) => setNewImageUrl(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Note (optional)"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="number"
            step="0.01"
            min="0"
            placeholder="Price (optional)"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAdd}
              disabled={submitting || !newName.trim()}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {submitting ? 'Adding…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                setNewName('');
                setNewUrl('');
                setNewImageUrl('');
                setNewNote('');
                setNewPrice('');
              }}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {items.length === 0 && !error && !showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          Your wishlist is empty. Add something above to save it for later.
        </div>
      )}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '12px 16px',
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
              }}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  style={{
                    width: '56px',
                    height: '56px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '8px',
                  background: '#e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  flexShrink: 0,
                }}>
                  🛍️
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{item.name}</strong>
                {typeof item.priceAtSave === 'number' && (
                  <span style={{ marginLeft: '8px', fontSize: '13px', color: '#16a34a', fontWeight: 600 }}>
                    ${item.priceAtSave.toFixed(2)}
                  </span>
                )}
                {item.note && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{item.note}</p>
                )}
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block', marginTop: '4px', fontSize: '12px', color: '#3b82f6' }}
                  >
                    View product
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemove(item.id)}
                disabled={removing === item.id}
                style={{
                  padding: '6px 12px',
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: removing === item.id ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  flexShrink: 0,
                }}
              >
                {removing === item.id ? '…' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
